import { CurrencyPipe } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
    name: 'format',
    pure: false,
    standalone: false
})
export class FormatPipe implements PipeTransform {
  transform(value: any, format: FormatType): string {
    try {
      // Get language preference from sessionStorage
      const languagePreference = sessionStorage.getItem('languagepreference');
      const locale = this.getLocale(languagePreference);

      if (format === 'date') {
        // Use Intl.DateTimeFormat for proper locale-aware date formatting
        // This doesn't require locale data registration
        if (value) {
          // Handle string dates in ISO 8601 format (yyyy-MM-ddThh:mm:ss)
          // new Date() constructor handles ISO 8601 format strings correctly
          const date = new Date(value);

          if (!isNaN(date.getTime())) {
            return new Intl.DateTimeFormat(locale, {
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
            }).format(date);
          }
        }
        return '';
      } else if (format === 'dateTime') {
        // Use Intl.DateTimeFormat for locale-aware date-time formatting
        if (value) {
          // Handle string dates in ISO 8601 format (yyyy-MM-ddThh:mm:ss)
          // new Date() constructor handles ISO 8601 format strings correctly
          const date = new Date(value);

          //test
          if (!isNaN(date.getTime())) {
            const formatted = new Intl.DateTimeFormat(locale, {
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
              hour12: true,
            }).format(date);

            return formatted.replace(',', '');
          }
        }
        return '';
      } else if (format === 'currency') {
        const currencyPipe: CurrencyPipe = new CurrencyPipe(locale);
        return currencyPipe.transform(value) || '';
      } else if (format === 'phone') {
        const temp: string = <any>value;
        if (temp?.length !== 10) {
          return temp;
        }

        const areaCode = temp.slice(0, 3);
        const exchange = temp.slice(3, 6);
        const lineNumber = temp.slice(6, 10);
        return `(${areaCode}) ${exchange}-${lineNumber}`;
      }
    } catch (error) {
      // Silently return original value on error
    }

    return value;
  }

  /**
   * Maps language code to locale string
   * @param languageCode Language preference from sessionStorage (e.g., 'en', 'es', 'fr')
   * @returns Locale string for Intl.DateTimeFormat (e.g., 'en-US', 'es-US', 'fr-FR')
   */
  private getLocale(languageCode: string | null): string {
    if (!languageCode || languageCode === 'en') {
      return 'en-US';
    }

    // Map common language codes to locale strings
    // Intl.DateTimeFormat supports most common locale formats
    const localeMap: { [key: string]: string } = {
      es: 'es-US', // Spanish (US)
      fr: 'fr-FR', // French
      de: 'de-DE', // German
      zh: 'zh-CN', // Chinese (Simplified)
      ja: 'ja-JP', // Japanese
      pt: 'pt-BR', // Portuguese (Brazil)
      it: 'it-IT', // Italian
      ko: 'ko-KR', // Korean
      ar: 'ar-SA', // Arabic
      ru: 'ru-RU', // Russian
      nl: 'nl-NL', // Dutch
      pl: 'pl-PL', // Polish
      sv: 'sv-SE', // Swedish
      tr: 'tr-TR', // Turkish
      hi: 'hi-IN', // Hindi
      th: 'th-TH', // Thai
      vi: 'vi-VN', // Vietnamese
      id: 'id-ID', // Indonesian
      he: 'he-IL', // Hebrew
      cs: 'cs-CZ', // Czech
      hu: 'hu-HU', // Hungarian
      ro: 'ro-RO', // Romanian
      fi: 'fi-FI', // Finnish
      da: 'da-DK', // Danish
      no: 'no-NO', // Norwegian
      el: 'el-GR', // Greek
    };

    const lowerCode = languageCode.toLowerCase();

    // If we have a direct mapping, use it
    if (localeMap[lowerCode]) {
      return localeMap[lowerCode];
    }

    // For unmapped language codes, return the language code
    // Intl.DateTimeFormat will attempt to use it, or fall back gracefully
    // If that fails, browsers will fall back to a supported locale
    return lowerCode;
  }
}

export type FormatType = 'date' | 'dateTime' | 'currency' | 'phone';
